import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useUser } from './UserContext';
import { bookmarkApi, highlightApi } from '../services/api';
import { applyRange, mergeVerseHighlights, toVersePayload } from '../services/highlights';
import { bookmarksKey, fetchChapterHighlights, highlightsKey } from '../hooks/queries';
import type { Bookmark, VerseHighlights, VerseHighlightsPayload, VerseRef } from '../types';

// The API takes at most this many verses in one highlights request.
const MAX_VERSES_PER_REQUEST = 500;

interface LocalAnnotations {
  // chapterKey(bibleId, bookId, chapter) -> that chapter's highlights
  highlights: Record<string, VerseHighlights>;
  // Newest first, like the API's list
  bookmarks: Bookmark[];
}

interface AnnotationsContextValue {
  localHighlights: Record<string, VerseHighlights>;
  updateLocalHighlights: (chapterKey: string, changes: VerseHighlights) => void;
  localBookmarks: Bookmark[];
  addLocalBookmark: (verse: VerseRef) => void;
  removeLocalBookmark: (bookmark: Bookmark) => void;
}

const EMPTY_LOCAL: LocalAnnotations = { highlights: {}, bookmarks: [] };

const AnnotationsContext = createContext<AnnotationsContextValue | null>(null);

/**
 * Paints the anonymous reader's highlights over whatever the account already has in those verses, as if they had been
 * made while logged in, then adds their bookmarks.
 */
async function saveToAccount(queryClient: QueryClient, userId: number, pending: LocalAnnotations) {
  const verses: VerseHighlightsPayload[] = [];
  for (const [key, localByVerse] of Object.entries(pending.highlights)) {
    const [bibleId, bookId, chapter] = key.split(':').map(Number);
    const saved = await queryClient.fetchQuery({
      queryKey: highlightsKey(userId, bibleId, bookId, chapter),
      queryFn: () => fetchChapterHighlights(bibleId, bookId, chapter),
    });
    for (const [verse, ranges] of Object.entries(localByVerse)) {
      const merged = ranges.reduce(
        (acc, range) => applyRange(acc, range.start, range.end, range.color),
        saved[Number(verse)] ?? [],
      );
      verses.push(toVersePayload(bibleId, bookId, chapter, Number(verse), merged));
    }
  }
  for (let i = 0; i < verses.length; i += MAX_VERSES_PER_REQUEST) {
    await highlightApi.replace(verses.slice(i, i + MAX_VERSES_PER_REQUEST));
  }

  // Oldest first, so the account lists them in the order they were made. Adding an already bookmarked verse is a no-op.
  for (const bookmark of [...pending.bookmarks].reverse()) {
    await bookmarkApi.create(bookmark.canonicalBookId, bookmark.chapterNumber, bookmark.verseNumber);
  }

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['highlights', userId] }),
    queryClient.invalidateQueries({ queryKey: bookmarksKey(userId) }),
  ]);
}

/**
 * Highlights and bookmarks of a reader who is not logged in. They live only in this provider's state: a reload drops
 * them, but going to /login or /signup and back does not, which is what lets them be saved to the account the reader
 * then logs into. A logged-in reader's annotations are server state, held by TanStack Query (see hooks/annotations.ts).
 */
export function AnnotationsProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useUser();
  const [local, setLocal] = useState<LocalAnnotations>(EMPTY_LOCAL);
  const localRef = useRef(local);
  const nextLocalBookmarkId = useRef(-1);

  const userId = isAuthenticated() && user ? user.id : null;

  useEffect(() => {
    localRef.current = local;
  }, [local]);

  useEffect(() => {
    if (userId === null) {
      // Logged out: forget the previous account's annotations so they are not shown to whoever reads next in this tab.
      queryClient.removeQueries({ queryKey: ['highlights'] });
      queryClient.removeQueries({ queryKey: ['bookmarks'] });
      return;
    }

    const pending = localRef.current;
    if (Object.keys(pending.highlights).length === 0 && pending.bookmarks.length === 0) return;

    // Cleared up front, so nothing is sent twice if this runs again before the requests finish.
    localRef.current = EMPTY_LOCAL;
    setLocal(EMPTY_LOCAL);
    saveToAccount(queryClient, userId, pending).catch((err) => {
      console.error('Failed to save highlights and bookmarks to the account:', (err as Error).message);
      // Keep them rather than lose them; they are sent again on the next login.
      setLocal(pending);
    });
  }, [userId, queryClient]);

  const updateLocalHighlights = useCallback((chapterKey: string, changes: VerseHighlights) => {
    setLocal(prev => {
      const chapter = mergeVerseHighlights(prev.highlights[chapterKey] ?? {}, changes);
      const highlights = { ...prev.highlights };
      if (Object.keys(chapter).length > 0) {
        highlights[chapterKey] = chapter;
      } else {
        delete highlights[chapterKey];
      }
      return { ...prev, highlights };
    });
  }, []);

  const addLocalBookmark = useCallback((verse: VerseRef) => {
    const id = nextLocalBookmarkId.current--;
    setLocal(prev => {
      const exists = prev.bookmarks.some(b =>
        b.canonicalBookId === verse.canonicalBookId &&
        b.chapterNumber === verse.chapterNumber &&
        b.verseNumber === verse.verseNumber
      );
      return exists ? prev : { ...prev, bookmarks: [{ id, ...verse }, ...prev.bookmarks] };
    });
  }, []);

  const removeLocalBookmark = useCallback((bookmark: Bookmark) => {
    setLocal(prev => ({ ...prev, bookmarks: prev.bookmarks.filter(b => b.id !== bookmark.id) }));
  }, []);

  const value: AnnotationsContextValue = {
    localHighlights: local.highlights,
    updateLocalHighlights,
    localBookmarks: local.bookmarks,
    addLocalBookmark,
    removeLocalBookmark,
  };

  return (
    <AnnotationsContext.Provider value={value}>
      {children}
    </AnnotationsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAnnotations() {
  const context = useContext(AnnotationsContext);
  if (!context) {
    throw new Error('useAnnotations must be used within an AnnotationsProvider');
  }
  return context;
}

export default AnnotationsContext;
